import React from "react";
import {
  Spin,
  Upload,
  message,
  Form,
  Input,
  Button,
  Select,
  Modal,
  Checkbox,
  Switch,
} from "antd";
import { LoadingOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../common/utils/notification";
import moment from "moment";
import {
  _add_restaurant,
  _get_vendor_restaurant,
  _update_restaurant_profile,
} from "../../common/axios_services";
import { suggestedBanners } from "../../common/constant";
import StateLgaSelects from "../../components/common/StateLgaSelects";
import useUserProfile from "../../hooks/useUserProfile";
import AutoCompleteAddress from "../../components/AutoCompleteAddress";

const RestaurantBusiness = ({ user }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );

  const { userProfile: fetchedUserProfile, refetchUserProfile } =
    useUserProfile();

  const [restaurantError, setRestaurantError] = React.useState("");
  const [spinnerLoading, setSpinnerLoading] = React.useState(false);
  const [banner_url, setBannerUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [updatingProfile, setUpdatingProfile] = React.useState(false);
  const [vendorRestaurant, setVendorRestaurant] = React.useState(false);
  const [updateCount, setUpdateCount] = React.useState(0);
  const [createMenuModal, setCreateMenuModal] = React.useState(false);
  const [isBannerModalOpen, setIsBannerModalOpen] = React.useState(false);
  const [showDifferentPickupAddress, setShowDifferentPickupAddress] =
    React.useState(false);
  const [pickupAddressExists, setPickupAddressExists] = React.useState(false);
  const [contactModalVisible, setContactModalVisible] = React.useState(false);
  const [contactPerson, setContactPerson] = React.useState("");
  const [contactTelephone, setContactTelephone] = React.useState("");
  const [contactForm] = Form.useForm();
  const [pickupAddressSelectedViaAutocomplete, setPickupAddressSelectedViaAutocomplete] =
    React.useState(false);

  const showSuggestions = () => {
    setIsBannerModalOpen(true);
  };

  const selectBanner = (url) => {
    setBannerUrl(url);
    setIsBannerModalOpen(false);
  };

  const handleModalClose = () => {
    setIsBannerModalOpen(false);
  };

  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.readAsDataURL(img);
    reader.onload = () => callback(reader.result);
    reader.onerror = (error) => console.error("Error reading file:", error);
  };

  const preBannerUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return Upload.LIST_IGNORE;
    }

    const isLt5MB = file.size / 1024 / 1024 < 5;
    if (!isLt5MB) {
      message.error("Image must be smaller than 5MB!");
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const uploadButton = (
    <div
      style={{
        width: "100%",
        height: "300px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload Banner</div>
    </div>
  );

  const finishProfile = async (values) => {
    const data = {
      name: values.name,
      bannerImage: banner_url || "",
      address: values.address,
      minReadyTime: values.ready_time,
      openTime: moment(values.open_time, "h:mm A").format("HH:mm"),
      closeTime: moment(values.close_time, "h:mm A").format("HH:mm"),
      isSundayOpen: Boolean(values.is_sunday_open),
      sundayOpenTime: values.is_sunday_open && values.sunday_open_time ? moment(values.sunday_open_time, "h:mm A").format("HH:mm") : null,
      sundayCloseTime: values.is_sunday_open && values.sunday_close_time ? moment(values.sunday_close_time, "h:mm A").format("HH:mm") : null,
      description: values.description || "",
      sellMode: values.sell_mode || "both",
    };

    if (values.showDifferentPickupAddress) {
      data.pickupAddressString = values.pickupAddressString;
      data.pickupState = values.pickupState;
      data.pickupLga = values.pickupLga;
      if (values.latitude) data.latitude = values.latitude;
      if (values.longitude) data.longitude = values.longitude;
    } else {
      data.pickupAddressString = user?.business?.address;
      data.pickupState = user?.business?.state;
      data.pickupLga = user?.business?.lga;
    }

    try {
      setUpdatingProfile(true);
      if (vendorRestaurant) {
        const updateRestaurantProfile = await _update_restaurant_profile(data);
        showSuccessNotification(updateRestaurantProfile?.data?.message);
      } else {
        const addRestaurant = await _add_restaurant(data);
        showSuccessNotification(addRestaurant?.data?.message);
        setVendorRestaurant(addRestaurant?.data?.data);
      }
      refetchUserProfile();
      // navigate(AllAppRoutes.restaurant_dashboard);
      setUpdateCount(updateCount + 1);
    } catch (err) {
      showErrorNotification(err.response.data.message ?? err.message);
    } finally {
      setUpdatingProfile(false);
    }
  };

  const fetchRestaurant = async () => {
    try {
      setSpinnerLoading(true);
      const restaurantResponse = await _get_vendor_restaurant();

      const restaurantData = restaurantResponse.data.data || {};
      const userProfileData = fetchedUserProfile || {};

      setVendorRestaurant(restaurantData || false);
      setBannerUrl(restaurantData?.bannerImage);
      setContactPerson(
        restaurantData?.contactPerson || userProfileData?.userName || ""
      );
      setContactTelephone(
        restaurantData?.contactTelephone || userProfileData?.phoneNumber || ""
      );

      if (restaurantData?.pickupAddressString) {
        setShowDifferentPickupAddress(true);
        setPickupAddressExists(true);
      } else {
        setShowDifferentPickupAddress(false);
        setPickupAddressExists(false);
      }

      form.setFieldsValue({
        name: restaurantData?.name || user?.business?.name,
        address: restaurantData?.address,
        ready_time: restaurantData?.minReadyTime
          ? restaurantData.minReadyTime
          : null,
        open_time: restaurantData?.openTime
          ? moment(restaurantData.openTime, "HH:mm").format("h:mm A")
          : null,
        close_time: restaurantData?.closeTime
          ? moment(restaurantData.closeTime, "HH:mm").format("h:mm A")
          : null,
        is_sunday_open: restaurantData?.isSundayOpen ?? false,
        sunday_open_time: restaurantData?.sundayOpenTime
          ? moment(restaurantData.sundayOpenTime, "HH:mm").format("h:mm A")
          : null,
        sunday_close_time: restaurantData?.sundayCloseTime
          ? moment(restaurantData.sundayCloseTime, "HH:mm").format("h:mm A")
          : null,
        pickupAddressString: restaurantData?.pickupAddressString,
        pickupState: restaurantData?.pickupState,
        pickupLga: restaurantData?.pickupLga,
        showDifferentPickupAddress: restaurantData?.pickupAddressString
          ? true
          : false,
        description: restaurantData?.description || "",
        sell_mode: restaurantData?.sellMode || "both",
      });
    } catch (err) {
      setVendorRestaurant(false);
      setContactPerson(fetchedUserProfile?.userName || "");
      setContactTelephone(fetchedUserProfile?.phoneNumber || "");

      form.setFieldsValue({
        name: user?.business?.name,
        address: fetchedUserProfile?.business?.address,
        state: fetchedUserProfile?.business?.state,
        lga: fetchedUserProfile?.business?.lga,
        description: "",
      });
    } finally {
      setSpinnerLoading(false);
    }
  };

  React.useEffect(() => {
    if (fetchedUserProfile) {
      fetchRestaurant();
    }
  }, [fetchedUserProfile, updateCount]);

  const generateOpenTimeOptions = () => {
    const times = [];
    for (let i = 8; i <= 14; i++) {
      const time = moment({ hour: i, minute: 0 });
      times.push(time.format("h:mm A"));
    }
    return times;
  };

  const generateCloseTimeOptions = () => {
    const times = [];
    for (let i = 12; i <= 23; i++) {
      const time = moment({ hour: i, minute: 0 });
      times.push(time.format("h:mm A"));
    }
    return times;
  };

  const readyTimeOptions = [15, 30, 45];
  const openTimeOptions = generateOpenTimeOptions();
  const closeTimeOptions = generateCloseTimeOptions();

  const { Option } = Select;

  const handleContactModalOpen = () => {
    contactForm.setFieldsValue({
      contact_person: contactPerson,
      contact_telephone: contactTelephone,
    });
    setContactModalVisible(true);
  };

  const handleContactModalClose = () => {
    setContactModalVisible(false);
    contactForm.resetFields();
  };

  const handleContactSave = async (values) => {
    try {
      setUpdatingProfile(true);
      const updateData = {
        contactPerson: values.contact_person,
        contactTelephone: values.contact_telephone,
      };

      const updateRestaurantProfile = await _update_restaurant_profile(
        updateData
      );
      showSuccessNotification(updateRestaurantProfile?.data?.message);

      setContactPerson(values.contact_person);
      setContactTelephone(values.contact_telephone);
      handleContactModalClose();
      refetchUserProfile();
    } catch (err) {
      showErrorNotification(err.response?.data?.message ?? err.message);
    } finally {
      setUpdatingProfile(false);
    }
  };

  return (
    <Spin spinning={spinnerLoading} indicator={newAntIcon}>
      <div
        style={{ marginBottom: "20px" }}
        className="avatar-uploader"
        onClick={showSuggestions}
      >
        <Upload
          name="banner"
          showUploadList={false}
          multiple={false}
          beforeUpload={() => false}
          style={{ pointerEvents: "none", width: "100%" }}
        >
          {banner_url ? (
            <img
              src={banner_url}
              alt="banner"
              style={{
                width: "100%",
                height: "300px",
                objectFit: "cover",
              }}
            />
          ) : (
            uploadButton
          )}
        </Upload>
      </div>

      <Modal
        centered
        open={isBannerModalOpen}
        title="Choose a Banner"
        onCancel={handleModalClose}
        footer={null}
        width="80vw"
        bodyStyle={{
          maxHeight: "70vh",
          overflowY: "auto",
          padding: "16px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: "15px",
            padding: "10px",
          }}
        >
          {suggestedBanners.map((banner) => (
            <div
              key={banner}
              style={{
                position: "relative",
                cursor: "pointer",
                border:
                  banner === banner_url
                    ? "2px solid #1890ff"
                    : "1px solid #ddd",
                borderRadius: "4px",
                overflow: "hidden",
                aspectRatio: "16/9",
              }}
              onClick={() => {
                selectBanner(banner);
                handleModalClose();
              }}
            >
              <img
                src={banner}
                alt="suggested-banner"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={handleModalClose}>Close</Button>
        </div>
      </Modal>

      <Form
        form={form}
        layout="vertical"
        onFinish={finishProfile}
        initialValues={{ name: user?.business?.name }}
        style={{ padding: "20px" }}
      >
        {restaurantError && <p className="error-message">{restaurantError}</p>}

        <p className="profile_category_title">Contact Information</p>
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Contact Person</p>
                  <p className="font-medium text-gray-900">
                    {contactPerson || "Not set"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    Contact Telephone
                  </p>
                  <p className="font-medium text-gray-900">
                    {contactTelephone || "Not set"}
                  </p>
                </div>
              </div>
            </div>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={handleContactModalOpen}
              className="text-gray-600 hover:text-gray-900"
            >
              Change
            </Button>
          </div>
        </div>

        <Form.Item
          label="Restaurant name"
          name="name"
          rules={[
            {
              required: true,
              message: "Restaurant name is required",
            },
          ]}
        >
          <Input placeholder="Enter restaurant name" />
        </Form.Item>

        <p className="profile_category_title">Restaurant location</p>
        <div>
          <Form.Item
            label="Main Restaurant Address"
            name="address"
            rules={[
              {
                required: true,
                message: "Please fill input is required",
              },
            ]}
          >
            <Input type="text" style={{ height: "5rem" }} disabled />
          </Form.Item>
        </div>

        <Form.Item name="showDifferentPickupAddress" valuePropName="checked">
          <Checkbox
            checked={showDifferentPickupAddress}
            onChange={(e) => {
              setShowDifferentPickupAddress(e.target.checked);
            }}
            disabled={pickupAddressExists}
          >
            Pickup address, different from business address
          </Checkbox>
        </Form.Item>

        {showDifferentPickupAddress && (
          <>
            <Form.Item
              label="Pickup Address"
              name="pickupAddressString"
              rules={[
                {
                  required: true,
                  message: "Please select a pickup address from the suggestions",
                },
              ]}
            >
              <AutoCompleteAddress
                placeholder="Search for pickup address..."
                style={{ height: "5rem" }}
                onAddressSelect={(addressData) => {
                  setPickupAddressSelectedViaAutocomplete(true);
                  form.setFieldsValue({
                    pickupAddressString:
                      addressData.formattedAddress ||
                      addressData.address ||
                      form.getFieldValue("pickupAddressString"),
                    latitude: addressData.latitude,
                    longitude: addressData.longitude,
                  });
                }}
              />
            </Form.Item>

            <StateLgaSelects
              form={form}
              initialPickupState={form.getFieldValue("pickupState")}
              initialPickupLga={form.getFieldValue("pickupLga")}
            />

            {pickupAddressSelectedViaAutocomplete && (
              <div
                style={{
                  marginBottom: 16,
                  padding: 12,
                  backgroundColor: "#e6f7ff",
                  border: "1px solid #91d5ff",
                  borderRadius: 4,
                }}
              >
                <span style={{ color: "#1890ff", fontWeight: "bold" }}>
                  ✓ Street address and coordinates verified via Google Maps
                </span>
              </div>
            )}

            <Form.Item name="latitude" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="longitude" hidden>
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item
          label="How do you sell?"
          name="sell_mode"
          rules={[
            {
              required: true,
              message: "Please select your selling mode!",
            },
          ]}
        >
          <Select
            placeholder="Select selling mode"
            size="large"
            style={{ width: "100%" }}
          >
            <Option value="online">Sell online only</Option>
            <Option value="venue">Sell from a public venue</Option>
            <Option value="both">Both</Option>
          </Select>
        </Form.Item>

        <p className="profile_category_title">Restaurant cooking time</p>

        <div>
          <Form.Item
            label="Average ready time"
            name="ready_time"
            rules={[
              {
                required: true,
                message: "Please select your ready time!",
              },
            ]}
          >
            <Select
              placeholder="Select ready time"
              size="large"
              style={{ width: "100%" }}
            >
              {readyTimeOptions.map((time) => (
                <Option key={time} value={time}>
                  {time} minutes
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="form-flex-2">
          <Form.Item
            label="Open time"
            name="open_time"
            dependencies={["close_time"]}
            rules={[
              {
                required: true,
                message: "Please select your restaurant open time!",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (
                    !value ||
                    !getFieldValue("close_time") ||
                    moment(value, "h:mm A").isBefore(
                      moment(getFieldValue("close_time"), "h:mm A")
                    )
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Open time must be before close time!")
                  );
                },
              }),
            ]}
          >
            <Select
              placeholder="Select open time"
              size="large"
              style={{ width: "100%" }}
            >
              {openTimeOptions.map((time) => (
                <Option key={time} value={time}>
                  {time}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Close time"
            name="close_time"
            dependencies={["open_time"]}
            rules={[
              {
                required: true,
                message: "Please select your restaurant close time!",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (
                    !value ||
                    !getFieldValue("open_time") ||
                    moment(getFieldValue("open_time"), "h:mm A").isBefore(
                      moment(value, "h:mm A")
                    )
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Close time must be after open time!")
                  );
                },
              }),
            ]}
          >
            <Select
              placeholder="Select close time"
              size="large"
              style={{ width: "100%" }}
            >
              {closeTimeOptions.map((time) => (
                <Option key={time} value={time}>
                  {time}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Sunday Operating Hours</h4>
              <p className="text-gray-600">Enable if your business opens on Sundays (OFF by default)</p>
            </div>
            <Form.Item name="is_sunday_open" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
          </div>

          <Form.Item shouldUpdate={(prev, curr) => prev.is_sunday_open !== curr.is_sunday_open} noStyle>
            {({ getFieldValue }) =>
              getFieldValue("is_sunday_open") ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <Form.Item label="Sunday Open time" name="sunday_open_time">
                    <Select placeholder="Select Sunday open time" size="large" style={{ width: "100%" }}>
                      {openTimeOptions.map((time) => (
                        <Option key={time} value={time}>
                          {time}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item label="Sunday Close time" name="sunday_close_time">
                    <Select placeholder="Select Sunday close time" size="large" style={{ width: "100%" }}>
                      {closeTimeOptions.map((time) => (
                        <Option key={time} value={time}>
                          {time}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              ) : null
            }
          </Form.Item>
        </div>

        <div>
          <Form.Item label="Description" name="description">
            <Input.TextArea
              placeholder="Enter description here..."
              rows={6}
              className="custom-textarea"
            />
          </Form.Item>
        </div>

        {updatingProfile || !banner_url ? (
          <button
            className="restaurant_profile_button bg-gray-300 text-gray-500 cursor-not-allowed border-none px-6 py-3 rounded-lg flex items-center justify-center transition-colors"
            disabled
          >
            {updatingProfile ? (
              <>
                Updating profile. Please wait
                <Spin style={{ marginLeft: "10px" }} indicator={newAntIcon} />
              </>
            ) : (
              <>Please upload a banner to proceed</>
            )}
          </button>
        ) : (
          <button
            className="restaurant_profile_button bg-blue-500 text-white hover:bg-blue-600 border-none px-6 py-3 rounded-lg transition-colors"
            htmlType="submit"
          >
            Update Profile
          </button>
        )}
      </Form>

      <Modal
        title="Note"
        open={createMenuModal}
        centered
        footer={null}
        onCancel={() => setCreateMenuModal(false)}
      >
        <p>
          Thank you for providing your restaurant profile.{" "}
          <b>Restaurants must add items to their menu to be approved.</b>
        </p>
        <button
          onClick={() => navigate("/Restaurant/menu")}
          className="restaurant_profile_button px-6 py-3 rounded-lg transition-colors bg-blue-500 text-white hover:bg-blue-600 border-none mt-3"
        >
          Create Item
        </button>
      </Modal>

      <Modal
        title="Update Contact Information"
        open={contactModalVisible}
        onCancel={handleContactModalClose}
        footer={null}
        centered
      >
        <Form form={contactForm} layout="vertical" onFinish={handleContactSave}>
          <Form.Item
            label="Contact Person"
            name="contact_person"
            rules={[
              {
                required: true,
                message: "Please enter contact person name",
              },
            ]}
          >
            <Input placeholder="Enter contact person name" />
          </Form.Item>

          <Form.Item
            label="Contact Telephone"
            name="contact_telephone"
            rules={[
              {
                required: true,
                message: "Please enter contact telephone",
              },
            ]}
          >
            <Input placeholder="Enter contact telephone" />
          </Form.Item>

          <div className="flex justify-end space-x-2 mt-4">
            <Button onClick={handleContactModalClose}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updatingProfile}
              className="bg-gray-900"
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>
    </Spin>
  );
};

export default RestaurantBusiness;
